import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

import { refreshAccessToken } from '../lib/api.js';
import { setForceLogout } from '../lib/authBridge.js';
import { clearToken, getToken, setToken } from '../lib/auth.js';

export interface AuthUser {
  sub: string;
  rol: string;
  correo: string;
}

function decodeJwtPayload(token: string): AuthUser | null {
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  try {
    const base64 = parts[1]!.replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(
      atob(base64).split('').map(c => `%${c.charCodeAt(0).toString(16).padStart(2, '0')}`).join(''),
    );
    return JSON.parse(json) as AuthUser;
  } catch {
    return null;
  }
}

interface AuthContextValue {
  token: string | null;
  user: AuthUser | null;
  // 'checking': intentando silent refresh al montar, todavía no se sabe si
  // hay sesión — 'ready': ya se sabe (autenticado o no). Un guard de ruta
  // NUNCA debe tratar 'checking' como "no autenticado".
  status: 'checking' | 'ready';
  isAuthenticated: boolean;
  login: (token: string) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

// Envuelve <App/> dentro del <BrowserRouter> (ver main.tsx). Al montar, si no
// hay accessToken en memoria/sessionStorage (se pierde al cerrar el
// navegador — SRS-01 §4.1), intenta un silent refresh contra /auth/refresh
// usando el refreshToken httpOnly (7 días, sobrevive el cierre) ANTES de que
// cualquier guard de ruta decida mostrar login.
export function AuthProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const [token, setTokenState] = useState<string | null>(() => getToken());
  const [status, setStatus] = useState<'checking' | 'ready'>(() => (getToken() ? 'ready' : 'checking'));

  useEffect(() => {
    if (token) return;
    let cancelled = false;
    refreshAccessToken().then(newToken => {
      if (cancelled) return;
      if (newToken) setTokenState(newToken);
      setStatus('ready');
    });
    return () => {
      cancelled = true;
    };
    // Solo al montar — una vez resuelto (o si ya había token), no repetir.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const user = useMemo(() => (token ? decodeJwtPayload(token) : null), [token]);

  const login = useCallback((newToken: string) => {
    setToken(newToken);
    setTokenState(newToken);
    setStatus('ready');
  }, []);

  const logout = useCallback(() => {
    clearToken();
    setTokenState(null);
    setStatus('ready');
    navigate('/login', { replace: true });
  }, [navigate]);

  // Conecta lib/api.ts (fuera de React) con este estado — al interceptar un
  // 403 CUENTA_INACTIVA, necesita invalidar user/token de inmediato, no solo
  // el storage subyacente (ver lib/authBridge.ts).
  useEffect(() => {
    setForceLogout(() => {
      clearToken();
      setTokenState(null);
      setStatus('ready');
    });
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ token, user, status, isAuthenticated: Boolean(token && user), login, logout }),
    [token, user, status, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth debe usarse dentro de <AuthProvider>');
  return ctx;
}
